import axios from 'axios';
import apiClient from './apiClient';
// Import achievement type from the achievement service
import { StudentAchievement } from './achievementService';

// Problem interfaces
export interface ProblemOption {
  id: number;
  problemId: number;
  optionText: string;
  isCorrect: boolean;
  optionOrder: number;
}

export interface Problem {
  id: number;
  sessionId: number;
  groupId: number;
  problemStatement: string;
  difficultyLevel: number;
  correctSolution?: string; // Only available for admins
  imagePath?: string;
  problemType: 'open_ended' | 'multiple_choice';
  options?: ProblemOption[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ProblemSubmission {
  id: number;
  problemId: number;
  userId: number;
  submittedAnswer: string;
  selectedOptionId?: number;
  submissionTime: Date;
  isCorrect: boolean;
  attemptCount: number;
}

// Get all problems
export const getAllProblems = async (): Promise<Problem[]> => {
  try {
    const response = await apiClient.get('/api/problems');
    return response.data;
  } catch (error) {
    console.error('Error fetching problems:', error);
    return [];
  }
};

// Get problems by session ID
export const getProblemsBySession = async (sessionId: number): Promise<Problem[]> => {
  try {
    const response = await apiClient.get(`/api/problems/session/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problems for session ${sessionId}:`, error);
    return [];
  }
};

// Get live problems by session ID (only from groups that are currently live)
export const getLiveProblemsBySession = async (sessionId: number): Promise<Problem[]> => {
  try {
    const response = await apiClient.get(`/api/problems/session/${sessionId}/live`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching live problems for session ${sessionId}:`, error);
    return [];
  }
};

// Get problems by group ID
export const getProblemsByGroup = async (groupId: number): Promise<Problem[]> => {
  try {
    const response = await apiClient.get(`/api/problems/group/${groupId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problems for group ${groupId}:`, error);
    return [];
  }
};

// Get problem by ID
export const getProblemById = async (problemId: number): Promise<Problem | null> => {
  try {
    const response = await apiClient.get(`/api/problems/${problemId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problem ${problemId}:`, error);
    return null;
  }
};

interface SubmitAnswerPayload {
  problemId: number;
  submittedAnswer?: string;
  selectedOptionId?: number;
}

// Submit an answer to a problem
export const submitAnswer = async (
  problemId: number,
  submittedAnswer: string | null,
  selectedOptionId?: number
): Promise<{
  submission: ProblemSubmission;
  message: string;
  newAchievements?: StudentAchievement[];
  achievementEarned?: boolean;
}> => {
  try {
    const payload: SubmitAnswerPayload = { problemId };

    // For multiple choice, we don't need submittedAnswer
    if (selectedOptionId !== undefined) {
      // This is a multiple choice submission
      payload.selectedOptionId = selectedOptionId;
      // Set empty string for submittedAnswer to keep the backend happy
      payload.submittedAnswer = '';
    } else if (submittedAnswer) {
      // This is an open-ended submission
      payload.submittedAnswer = submittedAnswer;
    }

    console.log('Sending payload to backend:', payload);

    const response = await apiClient.post(
      '/api/submissions/submit',
      payload
    );

    return response.data;
  } catch (error: unknown) {
    console.error('Error submitting answer:', error);
    const err = error as { response?: { data?: { message?: string } } };
    throw new Error(
      err.response?.data?.message ||
      'Failed to submit your answer. Please try again.'
    );
  }
};

// Get user's submission for a problem
export const getUserProblemSubmission = async (problemId: number): Promise<ProblemSubmission | ProblemSubmission[] | null> => {
  try {
    const response = await apiClient.get(`/api/submissions/problem/${problemId}`);
    // Backend returns array of submissions (there can be multiple attempts)
    return response.data;
  } catch (error) {
    // If 404, it means user hasn't submitted yet - that's ok
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    console.error('Error fetching user submission:', error);
    return null;
  }
};

// Get all user submissions
export const getUserSubmissions = async (): Promise<ProblemSubmission[]> => {
  try {
    const response = await apiClient.get('/api/submissions/my-submissions');
    return response.data;
  } catch (error) {
    console.error('Error fetching user submissions:', error);
    return [];
  }
};